/**
 * WelcomeSaleContext – 45-minute "Welcome Sale" window (25% off all passes)
 *
 * Eligibility is driven by the backend (welcome_sale_eligible flag from /api/auth/session).
 * The 45-minute countdown is a frontend urgency mechanism that starts when the user
 * first sees the sale (first authenticated page load where eligible=true).
 *
 * The backend does NOT enforce the 45-min window — it only checks:
 *  1. User has never used the welcome sale before (welcome_sale_used_at IS NULL)
 *  2. User has no prior successful subscription purchases
 *
 * This means existing users who never bought a pass ALSO get the offer.
 * The 45-min timer just creates urgency; the real gate is the one-time usage flag.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

// ─── Constants ──────────────────────────────────────────────────────────────────
const WELCOME_SALE_DURATION_MS = 45 * 60 * 1000; // 45 minutes
const WELCOME_SALE_DISCOUNT = 0.25; // 25% off
const LS_KEY_FIRST_SEEN = 'nc_welcome_sale_first_seen';
const LS_KEY_DISMISSED = 'nc_welcome_sale_dismissed';
const LS_KEY_USED = 'nc_welcome_sale_used'; // Set after purchase

// ─── Types ──────────────────────────────────────────────────────────────────────
interface WelcomeSaleState {
  /** Whether the 45-min welcome sale is currently active for this user */
  isActive: boolean;
  /** Whether the server says this user is eligible (regardless of timer) */
  isServerEligible: boolean;
  /** Minutes remaining (0–45) */
  minutesLeft: number;
  /** Seconds remaining within the current minute (0–59) */
  secondsLeft: number;
  /** Total milliseconds remaining */
  totalMsLeft: number;
  /** The discount multiplier (0.25 = 25% off) */
  discountRate: number;
  /** Calculate the discounted price for a given original price */
  getDiscountedPrice: (originalPrice: number) => number;
  /** The amount saved for a given original price */
  getSavings: (originalPrice: number) => number;
  /** Whether the banner was dismissed by the user */
  isDismissed: boolean;
  /** Dismiss the banner (sale stays active, just hides the banner) */
  dismiss: () => void;
  /** Mark the sale as used (after a purchase) */
  markUsed: () => void;
}

const WelcomeSaleContext = createContext<WelcomeSaleState | null>(null);

// ─── Provider ───────────────────────────────────────────────────────────────────
export function WelcomeSaleProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, hasActiveSubscription, welcomeSaleEligible } = useAuth();
  const [firstSeenAt, setFirstSeenAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [isDismissed, setIsDismissed] = useState(false);
  const [isUsed, setIsUsed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const userId = user?.id;

  // When user changes or on mount: load persisted state for this specific user
  useEffect(() => {
    if (!userId) {
      setFirstSeenAt(null);
      setIsDismissed(false);
      setIsUsed(false);
      return;
    }

    try {
      const dismissed = localStorage.getItem(`${LS_KEY_DISMISSED}_${userId}`);
      setIsDismissed(dismissed === 'true');

      const used = localStorage.getItem(`${LS_KEY_USED}_${userId}`);
      setIsUsed(used === 'true');

      const stored = localStorage.getItem(`${LS_KEY_FIRST_SEEN}_${userId}`);
      if (stored) {
        const ts = parseInt(stored, 10);
        if (!isNaN(ts)) {
          setFirstSeenAt(ts);
        }
      } else {
        setFirstSeenAt(null);
      }
    } catch {
      // localStorage not available
    }
  }, [userId]);

  // When user becomes eligible, record the first-seen timestamp for the countdown.
  // This fires for both new AND existing eligible users.
  useEffect(() => {
    if (!isAuthenticated || !welcomeSaleEligible || !userId) return;
    if (firstSeenAt) return; // Already recorded

    try {
      const stored = localStorage.getItem(`${LS_KEY_FIRST_SEEN}_${userId}`);
      if (stored) {
        const ts = parseInt(stored, 10);
        if (!isNaN(ts)) {
          setFirstSeenAt(ts);
          return;
        }
      }
    } catch {
      // ignore
    }

    // First time seeing this eligible user — start the timer now
    const timestamp = Date.now();
    setFirstSeenAt(timestamp);
    try {
      localStorage.setItem(`${LS_KEY_FIRST_SEEN}_${userId}`, String(timestamp));
    } catch {
      // ignore
    }
  }, [isAuthenticated, welcomeSaleEligible, userId, firstSeenAt]);

  // Tick every second while sale could be active
  useEffect(() => {
    if (!firstSeenAt || isUsed || hasActiveSubscription || !welcomeSaleEligible) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    const expiresAt = firstSeenAt + WELCOME_SALE_DURATION_MS;
    if (Date.now() >= expiresAt) return; // Already expired

    timerRef.current = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= expiresAt) {
        if (timerRef.current) clearInterval(timerRef.current);
      }
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [firstSeenAt, isUsed, hasActiveSubscription, welcomeSaleEligible]);

  // Compute derived state
  const expiresAt = firstSeenAt ? firstSeenAt + WELCOME_SALE_DURATION_MS : 0;
  const totalMsLeft = Math.max(0, expiresAt - now);

  // isActive requires BOTH server eligibility AND time remaining
  const isActive =
    isAuthenticated &&
    welcomeSaleEligible &&
    !hasActiveSubscription &&
    !isUsed &&
    firstSeenAt !== null &&
    totalMsLeft > 0;

  const totalSecondsLeft = Math.ceil(totalMsLeft / 1000);
  const minutesLeft = Math.floor(totalSecondsLeft / 60);
  const secondsLeft = totalSecondsLeft % 60;

  const getDiscountedPrice = useCallback(
    (originalPrice: number) => {
      if (!isActive) return originalPrice;
      return Math.round(originalPrice * (1 - WELCOME_SALE_DISCOUNT));
    },
    [isActive]
  );

  const getSavings = useCallback(
    (originalPrice: number) => {
      if (!isActive) return 0;
      return originalPrice - Math.round(originalPrice * (1 - WELCOME_SALE_DISCOUNT));
    },
    [isActive]
  );

  const dismiss = useCallback(() => {
    setIsDismissed(true);
    try {
      if (userId) localStorage.setItem(`${LS_KEY_DISMISSED}_${userId}`, 'true');
    } catch {
      // ignore
    }
  }, [userId]);

  const markUsed = useCallback(() => {
    setIsUsed(true);
    try {
      if (userId) localStorage.setItem(`${LS_KEY_USED}_${userId}`, 'true');
    } catch {
      // ignore
    }
  }, [userId]);

  const value: WelcomeSaleState = {
    isActive,
    isServerEligible: welcomeSaleEligible,
    minutesLeft,
    secondsLeft,
    totalMsLeft,
    discountRate: WELCOME_SALE_DISCOUNT,
    getDiscountedPrice,
    getSavings,
    isDismissed,
    dismiss,
    markUsed,
  };

  return (
    <WelcomeSaleContext.Provider value={value}>
      {children}
    </WelcomeSaleContext.Provider>
  );
}

// ─── Hook ───────────────────────────────────────────────────────────────────────
export function useWelcomeSale(): WelcomeSaleState {
  const context = useContext(WelcomeSaleContext);
  if (!context) {
    throw new Error('useWelcomeSale must be used within a WelcomeSaleProvider');
  }
  return context;
}
